import type {
  BillingPeriod,
  BillingStanding,
  ChargingModel,
  SubscriptionPaymentKind,
  SubscriptionPaymentMethod,
} from '@/api/generated/enums';
import type { StatusTone } from '@/components/StatusBadge';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';

import { periodWords } from './prices';
import type {
  BillingTermsInput,
  PaymentInput,
  Plan,
  PlanPrice,
  SuggestedPayment,
  TenantBilling,
} from './api';

/*
 * The rules of the company's billing screens (docs/Platform_Billing_Design.md §4–§8), pure and
 * tested. Each refusal here mirrors one in `PlatformBillingService`, so the panel never offers a
 * save the server would refuse; the wording maps are typed over the generated enums, so a new
 * member is a compile error, and the test asserts none is blank.
 */

/**
 * Today on this machine, as `YYYY-MM-DD`. The panel is used in India and the server's own "today"
 * is the Indian day, so the two agree; where a browser elsewhere disagrees by a day near midnight,
 * the server is the one that refuses, and says so.
 */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// ---- Words -------------------------------------------------------------------------------------

export const modelLabels: Record<ChargingModel, string> = {
  Subscription: 'Subscription',
  LicenceWithAmc: 'Licence + AMC',
  Pilot: 'Pilot',
};

export const periodLabels: Record<BillingPeriod, string> = {
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  Annual: 'Yearly',
};

export const kindLabels: Record<SubscriptionPaymentKind, string> = {
  Licence: 'Licence fee',
  Amc: 'AMC',
  Subscription: 'Subscription',
  Setup: 'Setup charge',
  Complimentary: 'Days given free',
};

export const methodLabels: Record<SubscriptionPaymentMethod, string> = {
  Upi: 'UPI',
  Cash: 'Cash',
  BankTransfer: 'Bank transfer',
  Cheque: 'Cheque',
};

/**
 * The due list's word for a company, and its tone. Red is late and amber needs attention, the
 * product's one colour rule, so a late company never rests on the colour alone — the word says it.
 */
export const standingWords: Record<BillingStanding, { label: string; tone: StatusTone }> = {
  NoTerms: { label: 'No terms set', tone: 'warning' },
  Pilot: { label: 'Pilot', tone: 'brand' },
  Paid: { label: 'Paid', tone: 'positive' },
  DueSoon: { label: 'Due soon', tone: 'warning' },
  Overdue: { label: 'Overdue', tone: 'negative' },
  ReadOnly: { label: 'Read-only', tone: 'negative' },
  Suspended: { label: 'Suspended', tone: 'negative' },
  NoSubscription: { label: 'No subscription', tone: 'negative' },
  Leaving: { label: 'Leaving', tone: 'neutral' },
};

/** Days between two `YYYY-MM-DD` dates, by the calendar, never by an instant. */
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

const addDays = (date: string, days: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

/** Same date, n years on, 29 February clamped to the 28th — .NET's `DateOnly.AddYears`, exactly. */
const addYears = (date: string, years: number) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const year = y + years;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const day = m === 2 && d === 29 && !leap ? 28 : d;

  return `${year}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * The sentence beside the badge — when, not just what. Worded from the server's standing and the
 * dates it sent, never re-deriving whether anything is late.
 */
export function standingSentence(billing: TenantBilling, today: string): string {
  const paid = billing.paidUntil;

  switch (billing.standing) {
    case 'NoTerms':
      return 'Provisioned before billing — set its terms, or nothing will ever fall due.';
    case 'Pilot':
      return `Free until ${formatDate(billing.terms?.pilotEndsOn ?? paid)}.`;
    case 'Paid':
      return `Paid until ${formatDate(paid)}.`;
    case 'DueSoon': {
      const days = paid ? daysBetween(today, paid) + 1 : 0;
      return `Paid until ${formatDate(paid)} — due ${days <= 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`}.`;
    }
    case 'Overdue':
      return paid
        ? `Overdue since ${formatDate(addDays(paid, 1))}. Reads only from ${formatDate(addDays(paid, billing.graceDays + 1))} unless it pays.`
        : 'Overdue.';
    case 'ReadOnly':
      return paid
        ? `Read-only: nothing paid after ${formatDate(paid)}. A payment makes it writable again at once.`
        : 'Read-only.';
    case 'Suspended':
      return 'Suspended by the platform. Its data is kept; nobody can sign in to it.';
    case 'NoSubscription':
      return 'No subscription is in effect.';
    case 'Leaving':
      return `Has given notice. Works until ${formatDate(paid)}, then reads only.`;
  }
}

/** The terms in one phrase: "₹900 a month, at list price" / "₹6,000 a year (list ₹9,000)". */
export function describeTerms(billing: TenantBilling): string {
  const terms = billing.terms;
  if (!terms) return 'No terms set.';
  if (terms.chargingModel === 'Pilot') return 'A pilot — free until the date it ends.';

  const rupees = (n: number | null) => `₹${formatAmount(n)}`;
  const recurring = `${rupees(terms.agreedRecurringAmount)} ${periodWords[terms.billingPeriod ?? 'Annual']}`;
  const whole =
    terms.chargingModel === 'LicenceWithAmc'
      ? `${rupees(terms.agreedOneTimeAmount)} licence, then ${recurring} AMC`
      : recurring;

  if (terms.listRecurringAmount === null) return `${whole} — no list price to compare.`;
  if (!terms.isNegotiated) return `${whole}, at list price.`;

  const list =
    terms.chargingModel === 'LicenceWithAmc'
      ? `list ${rupees(terms.listOneTimeAmount)} + ${rupees(terms.listRecurringAmount)}`
      : `list ${rupees(terms.listRecurringAmount)}`;

  return `${whole} (${list}).`;
}

// ---- Terms -------------------------------------------------------------------------------------

/** Every field as typed. Blank amounts take the list price; blank grace takes the model's default. */
export interface TermsDraft {
  planCode: string;
  chargingModel: ChargingModel;
  billingPeriod: BillingPeriod;
  recurring: string;
  oneTime: string;
  note: string;
  pilotEndsOn: string;
  grace: string;
}

export const maxGraceDays = 90;

/** 30 days for Licence + AMC (owner, 2026-10-18), 7 otherwise — `SubscriptionBilling.DefaultGraceDays`. */
export const defaultGraceDays = (model: ChargingModel) => (model === 'LicenceWithAmc' ? 30 : 7);

/** The one period a model may take: an AMC is yearly, a pilot has none. */
export const periodsFor = (model: ChargingModel): readonly BillingPeriod[] =>
  model === 'Subscription'
    ? ['Monthly', 'Quarterly', 'Annual']
    : model === 'LicenceWithAmc'
      ? ['Annual']
      : [];

export function termsDraftFrom(billing: TenantBilling): TermsDraft {
  const terms = billing.terms;
  const model = terms?.chargingModel ?? 'Subscription';

  return {
    planCode: billing.planCode ?? '',
    chargingModel: model,
    billingPeriod: terms?.billingPeriod ?? (model === 'LicenceWithAmc' ? 'Annual' : 'Monthly'),
    // Typed figures only where they differ from the list: an agreed-at-list price is left blank so
    // it keeps following the list when the terms are next changed.
    recurring:
      terms?.isNegotiated || terms?.listRecurringAmount === null
        ? String(terms?.agreedRecurringAmount ?? '')
        : '',
    oneTime:
      terms?.isNegotiated || terms?.listOneTimeAmount === null
        ? String(terms?.agreedOneTimeAmount ?? '')
        : '',
    note: terms?.priceNote ?? '',
    pilotEndsOn: terms?.pilotEndsOn ?? '',
    grace: terms ? String(billing.graceDays) : '',
  };
}

/** A new company's first terms: a monthly subscription at the package's list price. */
export function newTermsDraft(planCode: string): TermsDraft {
  return {
    planCode,
    chargingModel: 'Subscription',
    billingPeriod: 'Monthly',
    recurring: '',
    oneTime: '',
    note: '',
    pilotEndsOn: '',
    grace: '',
  };
}

/** What the model, period and plan list at — the figure a blank field takes. */
export function listPriceFor(plan: Plan | undefined, draft: TermsDraft): PlanPrice | undefined {
  return plan?.prices.find(
    (p) => p.chargingModel === draft.chargingModel && p.billingPeriod === draft.billingPeriod,
  );
}

const typedAmount = (typed: string): number | null => {
  const trimmed = typed.trim();
  return trimmed === '' ? null : Number(trimmed);
};

/** The amounts the server will freeze: typed, or the list price where nothing was typed. */
export function agreedAmounts(draft: TermsDraft, list: PlanPrice | undefined) {
  const recurring = typedAmount(draft.recurring) ?? list?.recurringAmount ?? null;
  const oneTime =
    draft.chargingModel === 'LicenceWithAmc'
      ? (typedAmount(draft.oneTime) ?? list?.oneTimeAmount ?? null)
      : null;

  return { recurring, oneTime };
}

/** Agreed differs from a list price that exists — the server's `IsNegotiated`. */
export function isNegotiatedDraft(draft: TermsDraft, list: PlanPrice | undefined): boolean {
  if (!list || draft.chargingModel === 'Pilot') return false;
  const { recurring, oneTime } = agreedAmounts(draft, list);

  return (
    (recurring !== null && recurring !== list.recurringAmount) ||
    (oneTime !== null && list.oneTimeAmount !== null && oneTime !== list.oneTimeAmount)
  );
}

export function termsProblems(draft: TermsDraft, plan: Plan | undefined, today: string): string[] {
  const problems: string[] = [];

  if (!draft.planCode) problems.push('Choose the package.');

  const grace = typedAmount(draft.grace);
  if (grace !== null && (!Number.isInteger(grace) || grace < 0 || grace > maxGraceDays)) {
    problems.push(`Grace must be a whole number of days, from 0 to ${maxGraceDays}.`);
  }

  if (draft.chargingModel === 'Pilot') {
    if (!draft.pilotEndsOn) problems.push('Give the date the pilot ends.');
    else if (draft.pilotEndsOn < today) problems.push('A pilot cannot end before today.');
    return problems;
  }

  const list = listPriceFor(plan, draft);
  const typedRecurring = typedAmount(draft.recurring);
  const typedOneTime = typedAmount(draft.oneTime);
  const { recurring, oneTime } = agreedAmounts(draft, list);

  if (
    [typedRecurring, draft.chargingModel === 'LicenceWithAmc' ? typedOneTime : null].some(
      (n) => n !== null && (Number.isNaN(n) || n <= 0),
    )
  ) {
    problems.push('Amounts must be more than zero.');
  } else if (recurring === null) {
    problems.push(
      draft.chargingModel === 'LicenceWithAmc'
        ? 'There is no list price for this, so type the yearly AMC agreed.'
        : 'There is no list price for this, so type the amount agreed.',
    );
  } else if (draft.chargingModel === 'LicenceWithAmc' && oneTime === null) {
    problems.push('There is no list price for the licence, so type the fee agreed.');
  }

  if (isNegotiatedDraft(draft, list) && !draft.note.trim()) {
    problems.push('Say why this company pays a different price from the list.');
  }

  return problems;
}

/** The request. Blank amounts go as null so the server takes the list price itself. */
export function termsRequest(draft: TermsDraft): BillingTermsInput {
  const pilot = draft.chargingModel === 'Pilot';
  const grace = typedAmount(draft.grace);

  return {
    planCode: draft.planCode,
    chargingModel: draft.chargingModel,
    billingPeriod: pilot ? null : draft.billingPeriod,
    agreedRecurringAmount: pilot ? null : typedAmount(draft.recurring),
    agreedOneTimeAmount:
      draft.chargingModel === 'LicenceWithAmc' ? typedAmount(draft.oneTime) : null,
    priceNote: draft.note.trim() || null,
    pilotEndsOn: pilot ? draft.pilotEndsOn : null,
    graceDays: grace,
  };
}

// ---- Payments ----------------------------------------------------------------------------------

/** What a payment may be for under each way of charging — the server's `KindFits`. */
export function kindsFor(model: ChargingModel | undefined): SubscriptionPaymentKind[] {
  switch (model) {
    case 'Subscription':
      return ['Subscription', 'Setup', 'Complimentary'];
    case 'LicenceWithAmc':
      return ['Licence', 'Amc', 'Setup', 'Complimentary'];
    case 'Pilot':
      return ['Setup', 'Complimentary'];
    default:
      return [];
  }
}

/**
 * Whether a kind of payment covers dates. Setup buys no time; a licence usually covers the first
 * year but may cover nothing; everything else is only ever time.
 */
export const coverOf = (kind: SubscriptionPaymentKind): 'none' | 'optional' | 'required' =>
  kind === 'Setup' ? 'none' : kind === 'Licence' ? 'optional' : 'required';

export interface PaymentDraft {
  kind: SubscriptionPaymentKind;
  receivedOn: string;
  amount: string;
  method: SubscriptionPaymentMethod | '';
  reference: string;
  coversFrom: string;
  coversTo: string;
  note: string;
}

/** The page's start: the server's suggestion when there is one, today and a blank otherwise. */
export function paymentDraftFrom(
  suggested: SuggestedPayment | null,
  model: ChargingModel | undefined,
  today: string,
): PaymentDraft {
  const kind = suggested?.kind ?? kindsFor(model)[0] ?? 'Setup';

  return {
    kind,
    receivedOn: today,
    amount: suggested ? String(suggested.amount) : '',
    method: kind === 'Complimentary' ? '' : 'Upi',
    reference: '',
    coversFrom: suggested?.coversFrom ?? '',
    coversTo: suggested?.coversTo ?? '',
    note: '',
  };
}

/** Changing the kind keeps what still applies and clears what no longer can. */
export function withKind(draft: PaymentDraft, kind: SubscriptionPaymentKind): PaymentDraft {
  const free = kind === 'Complimentary';
  const cover = coverOf(kind);

  return {
    ...draft,
    kind,
    amount: free ? '0' : draft.amount === '0' ? '' : draft.amount,
    method: free ? '' : draft.method || 'Upi',
    coversFrom: cover === 'none' ? '' : draft.coversFrom,
    coversTo: cover === 'none' ? '' : draft.coversTo,
  };
}

export const maxCoverYears = 5;

export function paymentProblems(draft: PaymentDraft, today: string): string[] {
  const problems: string[] = [];
  const free = draft.kind === 'Complimentary';
  const amount = typedAmount(draft.amount);

  if (!draft.receivedOn) problems.push('Give the date the money arrived.');
  else if (draft.receivedOn > today) problems.push('The money cannot have arrived after today.');

  if (free) {
    if (!draft.note.trim()) problems.push('Say why these days are given free.');
  } else {
    if (amount === null || Number.isNaN(amount) || amount <= 0) {
      problems.push('Amounts must be more than zero.');
    }
    if (!draft.method) problems.push('Say how the money arrived.');
  }

  const cover = coverOf(draft.kind);
  const hasFrom = draft.coversFrom !== '';
  const hasTo = draft.coversTo !== '';

  if (cover === 'required' && (!hasFrom || !hasTo)) {
    problems.push('Give the dates this payment covers.');
  } else if (cover === 'optional' && hasFrom !== hasTo) {
    problems.push('Give both ends of the cover, or neither.');
  } else if (hasFrom && hasTo) {
    if (draft.coversTo < draft.coversFrom) {
      problems.push('The cover ends before it starts.');
    } else if (draft.coversTo >= addYears(draft.coversFrom, maxCoverYears)) {
      problems.push(`One payment cannot cover more than ${maxCoverYears} years.`);
    }
  }

  return problems;
}

export function paymentRequest(draft: PaymentDraft): PaymentInput {
  const free = draft.kind === 'Complimentary';
  const cover = coverOf(draft.kind) !== 'none';

  return {
    kind: draft.kind,
    receivedOn: draft.receivedOn,
    amount: free ? 0 : Number(draft.amount.trim()),
    method: free || !draft.method ? null : draft.method,
    reference: draft.reference.trim() || null,
    coversFrom: cover && draft.coversFrom ? draft.coversFrom : null,
    coversTo: cover && draft.coversTo ? draft.coversTo : null,
    note: draft.note.trim() || null,
  };
}
